import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Stack,
  Alert,
  Collapse,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  useGetCourQuery,
  useUpdateCourPricesMutation,
} from "../../app/services/courApi";
import AddCoursModel from "./AddCoursModel";

function CoursTable({ bureauId }) {
  const { data, isLoading, isSuccess, isError, error } =
    useGetCourQuery(bureauId);
  const [updateCourPrices] = useUpdateCourPricesMutation();
  const [editableCours, setEditableCours] = useState([]);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [modifiedCoursIds, setModifiedCoursIds] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (data?.Bureau?.cours) {
      setEditableCours(
        data.Bureau.cours.map((cour) => ({
          ...cour,
          prixAchatTemp: cour.prixAchat,
          prixVenteTemp: cour.prixVente,
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    const status = error?.status || error?.originalStatus;

    if (status === 403) {
      setErrorMessage("Accès non autorisé.");
    } else if (status === 404) {
      setErrorMessage("Données introuvables.");
    } else if (status === 500) {
      setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
    } else if (isError) {
      setErrorMessage("Une erreur est survenue.");
    } else {
      setErrorMessage("");
    }
  }, [error, isError]);

  const handleEdit = (id, field, value) => {
    setEditableCours((prev) =>
      prev.map((cour) => (cour.id === id ? { ...cour, [field]: value } : cour))
    );
  };

  const handleSave = async (id) => {
    setIsLoadingSave(true);
    const courToSave = editableCours.find((cour) => cour.id === id);
    const { prixAchatTemp, prixVenteTemp } = courToSave;

    try {
      const response = await updateCourPrices({
        id,
        cour: {
          prixAchat: prixAchatTemp,
          prixVente: prixVenteTemp,
        },
      });

      setIsLoadingSave(false);

      if (!response.error) {
        setModifiedCoursIds((prev) => [...prev, id]);
        setErrorMessage(""); // clear previous errors
      } else {
        const status = response.error.status || response.error.originalStatus;
        if (status === 403) {
          setErrorMessage("Accès non autorisé.");
        } else {
          setErrorMessage("Erreur lors de la mise à jour.");
        }
      }
    } catch (err) {
      setIsLoadingSave(false);
      setErrorMessage("Erreur lors de l'enregistrement.");
      console.error("Erreur :", err);
    }
  };

  const getCellStyle = (originalValue, currentValue, id) => {
    if (modifiedCoursIds.includes(id)) {
      return { backgroundColor: "#c8e6c9" }; // vert clair : enregistré
    }
    return originalValue !== currentValue
      ? { backgroundColor: "#ffcccb" } // rouge clair : modifié mais non enregistré
      : {};
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      justifyContent="start"
      alignItems="flex-start"
      sx={{ padding: 2, flexWrap: "wrap" }}
    >
      <Box sx={{ width: "100%" }}>
        <Collapse in={!!errorMessage}>
          <Alert
            severity="error"
            onClose={() => setErrorMessage("")}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        <TableContainer sx={{ mt: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Pays</TableCell>
                <TableCell>Prix d'achat (MAD)</TableCell>
                <TableCell>Prix de vente (MAD)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editableCours.map((cour) => (
                <TableRow key={cour.id}>
                  <TableCell>
                    <img
                      src={`${process.env.REACT_APP_BASE_FILE_URL}/Devise/image/${cour.devise.image}`}
                      alt={cour.devise.name}
                      style={{ width: 40, height: 32 }}
                    />
                  </TableCell>
                  <TableCell>{cour.devise.name}</TableCell>
                  <TableCell>{cour.devise.pays}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={cour.prixAchatTemp}
                      onChange={(e) =>
                        handleEdit(cour.id, "prixAchatTemp", e.target.value)
                      }
                      inputProps={{
                        style: {
                          minWidth: "80px",
                        },
                      }}
                      sx={getCellStyle(
                        cour.prixAchat,
                        cour.prixAchatTemp,
                        cour.id
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={cour.prixVenteTemp}
                      onChange={(e) =>
                        handleEdit(cour.id, "prixVenteTemp", e.target.value)
                      }
                      inputProps={{
                        style: {
                          minWidth: "80px",
                        },
                      }}
                      sx={getCellStyle(
                        cour.prixVente,
                        cour.prixVenteTemp,
                        cour.id
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSave(cour.id)}
                      disabled={isLoadingSave}
                    >
                      {isLoadingSave ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Enregistrer"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ pt: 2 }}>
        <AddCoursModel Bureau={data?.Bureau} />
      </Box>
    </Stack>
  );
}

export default CoursTable;
